import React, { useMemo } from "react";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { toneChipSx, toneMain, statusTone, fillToneFor } from "./channelTheme";
import { formatDelta } from "./channelUtils";

const STATUS_ICONS = {
  alarm: "fa-solid fa-circle-exclamation",
  warning: "fa-solid fa-triangle-exclamation",
  ok: "fa-solid fa-circle-check",
  nodata: "fa-solid fa-circle-question",
};

export function ChannelStatusChip({ channel, size = "small" }) {
  const level = channel?.status?.level || "nodata";
  return (
    <Chip
      size={size}
      icon={<Icon className={STATUS_ICONS[level]} sx={{ width: "auto", overflow: "visible" }} />}
      label={channel?.status?.label || "No data"}
      sx={toneChipSx(statusTone(channel))}
    />
  );
}

// Shows the backend `state.display_label` (e.g. "Pumping", "Ignition on") when present.
export function BackendStateChip({ channel }) {
  if (!channel?.stateLabel) return null;
  const color = channel.stateColor;
  return (
    <Chip
      size="small"
      label={channel.stateLabel}
      sx={(theme) => {
        const base = toneChipSx("secondary")(theme);
        if (!color) return base;
        const main = theme.palette[color]?.main || color;
        return {
          ...base,
          color: main,
          backgroundColor: theme.functions.rgba(main, 0.14),
          border: `1px solid ${theme.functions.rgba(main, 0.3)}`,
        };
      }}
    />
  );
}

const downsample = (points, max = 60) => {
  if (points.length <= max) return points;
  const step = points.length / max;
  const out = [];
  for (let i = 0; i < max; i += 1) out.push(points[Math.floor(i * step)]);
  out[out.length - 1] = points[points.length - 1];
  return out;
};

export function ChannelSparkline({ points = [], tone = "info", height = 28 }) {
  const path = useMemo(() => {
    const pts = downsample(points);
    if (pts.length < 2) return null;
    const t0 = pts[0].t;
    const t1 = pts[pts.length - 1].t || t0 + 1;
    let min = Infinity;
    let max = -Infinity;
    pts.forEach(({ v }) => {
      if (v < min) min = v;
      if (v > max) max = v;
    });
    const span = max - min || 1;
    const line = pts
      .map(({ t, v }, i) => {
        const x = ((t - t0) / (t1 - t0 || 1)) * 100;
        const y = 2 + (1 - (v - min) / span) * 26;
        return `${i ? "L" : "M"}${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ");
    return { line, area: `${line} L100,30 L0,30 Z` };
  }, [points]);

  if (!path) {
    return (
      <MDBox height={height} display="flex" alignItems="center">
        <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
          No recent history
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox
      component="svg"
      viewBox="0 0 100 30"
      preserveAspectRatio="none"
      aria-hidden="true"
      sx={({ palette }) => ({ width: "100%", height, display: "block", color: toneMain(palette, tone) })}
    >
      <path d={path.area} fill="currentColor" opacity="0.14" />
      <path d={path.line} fill="none" stroke="currentColor" strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
    </MDBox>
  );
}

export function TankFill({ channel, height = 8 }) {
  const pct = Math.max(0, Math.min(100, channel?.value ?? 0));
  const tone = fillToneFor(channel);
  return (
    <MDBox
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${channel?.name} level`}
      sx={({ palette, functions: { rgba } }) => ({
        position: "relative",
        height,
        borderRadius: height,
        overflow: "hidden",
        backgroundColor: rgba(palette.text.main, 0.1),
      })}
    >
      <MDBox
        sx={({ palette }) => ({
          width: `${pct}%`,
          height: "100%",
          borderRadius: height,
          backgroundColor: toneMain(palette, tone),
          transition: "width 300ms ease",
        })}
      />
    </MDBox>
  );
}

export function DeltaText({ delta, unit, suffix = "24h" }) {
  const d = formatDelta(delta, unit);
  if (!d) return null;
  return (
    <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ whiteSpace: "nowrap" }}>
      {d.text}
      {suffix ? ` · ${suffix}` : ""}
    </MDTypography>
  );
}
